import React, { useContext, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
} from "react-router-dom";
import { AuthContext, AuthProvider } from "./context/AuthContext";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "react-hot-toast";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Login/Signup/Dashboard shell/Error are needed on first paint for most
// visitors, so they stay eagerly bundled. Everything behind a login (the
// role-specific dashboard pages) is only ever needed after auth + a route
// change, so it's split into its own chunk and loaded on demand - this
// keeps the initial bundle (and first paint) small.
import Login from "./components/Login";
import Signup from "./components/Signup";
import Dashboard from "./components/Dashboard";
import Error from "./components/Error";
import ForgotPassword from "./components/ForgotPassword";

const Overview = lazy(() => import("./components/Dashboards/Overview"));
const PDFgenerator = lazy(() => import("./component/PDFgenerator"));
const RegisteredUser = lazy(() => import("./component/RegisteredUser"));
const AllProducts = lazy(() => import("./components/Dashboards/UserDashboard/AllProducts"));
const ProfileDetails = lazy(() => import("./components/Dashboards/UserDashboard/ProfileDetails"));
const QuotationRequestPage = lazy(() => import("./components/Dashboards/UserDashboard/QuotationRequestPage"));
const MyQuotation = lazy(() => import("./components/Dashboards/UserDashboard/MyQuotation"));
const ModifyPDF = lazy(() => import("./components/Dashboards/AdminDashboard/ModifyPDF"));
const AddProduct = lazy(() => import("./components/Dashboards/AdminDashboard/AddProduct"));
const QuotationHistoryNew = lazy(() => import("./components/Dashboards/AdminDashboard/QuotationHistoryNew"));
const BusinessInfoListNew = lazy(() => import("./component/BusinessInfoListNew"));
const ViewProducts = lazy(() => import("./components/Dashboards/PartnerDashboard/ViewProducts"));
const PartnerProfile = lazy(() => import("./components/Dashboards/PartnerDashboard/PartnerProfile"));
const AllPartner = lazy(() => import("./components/Dashboards/AdminDashboard/AllPartner"));

const RouteFallback = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
  </div>
);

const App = () => {
  const { user } = useContext(AuthContext);
  return (
    <Router>
      {/* Single app-wide toast host. Every page used to mount its own
          <Toaster>, several without a position (react-hot-toast defaults to
          top-center), which sat directly on top of the PageHeader title on
          every page that has one. One instance here, positioned clear of
          the header, fixes that and removes the risk of a toast rendering
          twice through two simultaneously-mounted Toasters.

          The app uses two different toast libraries (react-hot-toast in
          most places, react-toastify in AddProduct/AllPartner/PartnerProfile
          /AuthContext) and the only <ToastContainer> (react-toastify's host)
          in the whole app lived on the logged-out Login/ForgotPassword
          screens - every toast fired from those dashboard pages had nowhere
          to render and silently disappeared. Mounting both hosts here fixes
          that; a follow-up could consolidate onto one library. */}
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <ToastContainer position="top-right" theme="colored" />
      <AnimatePresence>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/signup" element={<Signup />} />
          <Route element={<Dashboard />}>
            {user && <Route path="/dashboard" element={<Overview />} />}
            {user?.role === "user" && (
              <>
                <Route path="/all-products" element={<AllProducts />} />
                <Route path="/profile" element={<ProfileDetails />} />
                <Route
                  path="/quotation-request"
                  element={<QuotationRequestPage />}
                />
                <Route path="/quotation" element={<MyQuotation />} />
              </>
            )}

            {user?.role === "admin" && (
              <>
                <Route path="/quotation" element={<PDFgenerator />} />
                <Route path="/new-user" element={<RegisteredUser />} />
                <Route path="/all-user" element={<BusinessInfoListNew />} />

                <Route path="/history" element={<QuotationHistoryNew />} />
                <Route path="/edit/:id" element={<ModifyPDF />} />
                <Route path="/add-product" element={<AddProduct />} />
                <Route path="/all-partner" element={<AllPartner />} />
              </>
            )}

            {user?.role === "partner" && (
              <>
                <Route path="/quotation" element={<PDFgenerator />} />
                <Route path="/new-user" element={<RegisteredUser />} />
                <Route path="/all-user" element={<BusinessInfoListNew />} />

                <Route path="/history" element={<QuotationHistoryNew />} />
                <Route path="/edit/:id" element={<ModifyPDF />} />
                <Route path="/all-products" element={<ViewProducts />} />
                <Route path="/profile" element={<PartnerProfile />} />
              </>
            )}

            <Route path="*" element={<Error />} />
          </Route>
          </Routes>
        </Suspense>
      </AnimatePresence>
    </Router>
  );
};

const RootApp = () => (
  <AuthProvider>
    <App />
  </AuthProvider>
);

export default RootApp;
